import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PremiumEvidenceWidget } from "@/components/EvidenceIntelligence";
import {
  Activity, LayoutGrid, Radar, Bell, Users, Sparkles, RefreshCw, ArrowLeft, Send,
  AlertTriangle, TrendingUp, ChevronRight, Loader2, X, Gauge, ShieldCheck, Zap, DollarSign,
  FileSignature, Mic, FileDown, Check, Clock,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Executive Overview", icon: Activity },
  { key: "financials", label: "Financial Intelligence", icon: DollarSign },
  { key: "pipeline", label: "Mission Control", icon: LayoutGrid },
  { key: "intelligence", label: "Firm Intelligence", icon: Radar },
  { key: "evidence", label: "Evidence Intelligence", icon: ShieldCheck },
  { key: "retainers", label: "Retainer Intelligence", icon: FileSignature },
  { key: "voiceaudit", label: "Voice Audit", icon: Mic },
  { key: "alerts", label: "Alerts Center", icon: Bell },
  { key: "staff", label: "Staff Productivity", icon: Users },
  { key: "assistant", label: "AI Ops Assistant", icon: Sparkles },
];
const POLL_MS = 20000;
const fmt = (n) => (n === null || n === undefined ? null : typeof n === "number" ? n.toLocaleString() : n);
const money = (n) => (n === null || n === undefined ? null : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

function Ring({ value, size = 120, label = "HEALTH", color }) {
  const r = size / 2 - 10, circ = 2 * Math.PI * r, off = circ - ((value || 0) / 100) * circ;
  const c = color || (value >= 70 ? "#22d3ee" : value >= 40 ? "#f59e0b" : "#ef4444");
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="health-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1e293b" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c} strokeWidth="8" strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .8s ease", filter: `drop-shadow(0 0 6px ${c}88)` }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-extrabold text-white leading-none">{value ?? "—"}</span>
        <span className="text-[10px] tracking-[0.2em] text-slate-400 mt-1">{label}</span>
      </div>
    </div>
  );
}

function Kpi({ label, metric, kind, onClick }) {
  const nt = metric?.status === "not_tracked";
  const raw = metric?.value;
  const val = kind === "money" ? money(raw) : fmt(raw);
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`text-left rounded-xl border border-slate-800 bg-slate-900/60 p-4 transition-all ${onClick ? "hover:border-cyan-500/50 hover:bg-slate-900" : ""} ${nt ? "opacity-60" : ""}`}>
      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</div>
      {nt ? (
        <div className="mt-1" title={metric?.source_hint}>
          <div className="text-sm text-slate-500">Not tracked</div>
          <div className="text-[10px] text-slate-600">connect data source</div>
        </div>
      ) : (
        <div className="mt-1 text-2xl font-extrabold text-white tabular-nums">{val ?? "—"}</div>
      )}
    </button>
  );
}

function OverviewTab({ data, go }) {
  if (!data) return <Spinner />;
  const m = data.metrics;
  const maxRev = Math.max(1, ...(data.revenue_trend || []).map((x) => x.amount));
  const groups = [
    ["Case Load", [["Total Active Cases", "total_active_cases"], ["New Today", "new_cases_today"], ["High Value Cases", "high_value_cases"], ["Avg CRI™", "avg_cri"]]],
    ["Readiness", [["Demand Ready™", "demand_ready"], ["Settlement", "settlement_ready"], ["Litigation", "litigation_ready"], ["Trial", "trial_ready"]]],
    ["Risk & Ops", [["Cases At Risk", "cases_at_risk"], ["Treatment Gaps", "treatment_gap_alerts"], ["Missing Docs", "missing_documentation_alerts"], ["Critical Deadlines", "critical_deadlines"]]],
    ["Velocity", [["Avg Days To Demand", "avg_days_to_demand"], ["Avg Intake Response", "avg_intake_response_time"], ["Tasks Due Today", "tasks_due_today"], ["Tasks Overdue", "tasks_overdue"]]],
  ];
  return (
    <div className="space-y-6" data-testid="cc-overview">
      <div className="flex flex-wrap items-center gap-6 rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
        <Ring value={data.firm_health_score} />
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs tracking-widest text-cyan-400 uppercase">Firm Health Score</div>
          <p className="text-slate-300 mt-1 text-sm">Composite of average CRI™, case risk exposure, and open high-priority alerts.</p>
          <div className="mt-3 flex gap-6">
            <div><div className="text-2xl font-extrabold text-white">{data.open_alerts}</div><div className="text-[10px] text-slate-400 uppercase">Open Alerts</div></div>
            <div><div className="text-2xl font-extrabold text-red-400">{data.high_priority_alerts}</div><div className="text-[10px] text-slate-400 uppercase">High Priority</div></div>
          </div>
        </div>
        <div className="min-w-[240px]">
          <div className="text-[10px] tracking-widest text-slate-400 uppercase mb-2">Revenue Collected (6mo)</div>
          {(data.revenue_trend || []).length === 0 ? <div className="text-slate-500 text-sm">No collected revenue yet.</div> : (
            <div className="flex items-end gap-1.5 h-20">
              {data.revenue_trend.map((x) => (
                <div key={x.month} className="flex-1 flex flex-col items-center gap-1" title={`${x.month}: ${money(x.amount)}`}>
                  <div className="w-full bg-cyan-500/70 rounded-t" style={{ height: `${(x.amount / maxRev) * 100}%` }} />
                  <span className="text-[9px] text-slate-500">{x.month.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-1 text-sm text-emerald-400 font-bold">{money(m.revenue_collected?.value)} <span className="text-slate-500 font-normal text-xs">collected · {money(m.revenue_outstanding?.value)} outstanding</span></div>
        </div>
      </div>

      {groups.map(([title, items]) => (
        <div key={title}>
          <div className="text-xs font-semibold tracking-widest text-slate-500 uppercase mb-2">{title}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {items.map(([label, key]) => (
              <Kpi key={key} label={label} metric={m[key]} kind={key.includes("revenue") ? "money" : "num"}
                   onClick={["cases_at_risk", "demand_ready", "high_value_cases"].includes(key) ? () => go("pipeline") : null} />
            ))}
          </div>
        </div>
      ))}

      <div>
        <div className="text-xs font-semibold tracking-widest text-slate-500 uppercase mb-2">Financials (connect data sources to activate)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["Case Pipeline Value", "case_pipeline_value"], ["Projected Fee Pipeline", "projected_fee_pipeline"], ["Avg Settlement Est.", "avg_settlement_estimate"], ["Avg Days In Treatment", "avg_days_in_treatment"]].map(([l, k]) => (
            <Kpi key={k} label={l} metric={m[k]} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PipelineTab({ nav }) {
  const [board, setBoard] = useState(null);
  const [q, setQ] = useState("");
  const [attorney, setAttorney] = useState("");
  const [drag, setDrag] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get(`/command-center/pipeline?${attorney ? `attorney_id=${attorney}&` : ""}${q ? `q=${encodeURIComponent(q)}` : ""}`);
    setBoard(data);
  }, [q, attorney]);
  useEffect(() => { load(); }, [load]);
  const onDrop = async (stage) => {
    if (!drag) return;
    setDrag(null);
    await doMove([drag], stage);
  };
  const doMove = async (ids, stage) => {
    try { await api.post("/command-center/pipeline/move", { case_ids: ids, stage }); toast.success("Case moved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  if (!board) return <Spinner />;
  return (
    <div data-testid="cc-pipeline">
      <div className="flex flex-wrap gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search case / client…" data-testid="pipeline-search" className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500" />
        <select value={attorney} onChange={(e) => setAttorney(e.target.value)} data-testid="pipeline-attorney-filter" className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white outline-none">
          <option value="">All attorneys</option>
          {board.attorneys.map((a) => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
        </select>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {board.stages.map((s) => {
          const cards = board.columns[s.key] || [];
          return (
            <div key={s.key} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(s.key)} data-testid={`pipeline-col-${s.key}`}
              className="min-w-[240px] w-60 shrink-0 rounded-xl border border-slate-800 bg-slate-900/40">
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
                <span className="text-xs font-semibold text-slate-200">{s.label}</span>
                <span className="text-[10px] px-1.5 rounded-full bg-slate-800 text-slate-400">{cards.length}</span>
              </div>
              <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto">
                {cards.map((c) => (
                  <div key={c.case_id} draggable onDragStart={() => setDrag(c.case_id)} onClick={() => nav(`/intelligence/${c.case_id}`)}
                    data-testid={`pipeline-card-${c.case_id}`}
                    className="rounded-lg border border-slate-800 bg-slate-950 p-2.5 cursor-pointer hover:border-cyan-500/50">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[11px] text-cyan-300">{c.case_number}</span>
                      <span className="text-xs font-bold px-1.5 rounded" style={{ backgroundColor: `${c.grade_color}22`, color: c.grade_color }}>{c.grade}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5 truncate">{c.client_name || "—"}</div>
                    <div className="mt-1.5 flex items-center justify-between text-[10px]">
                      <span className="text-slate-300">CRI {c.score}</span>
                      {c.status === "demand_ready" ? <span className="text-emerald-400">Ready</span> :
                        c.missing_required > 0 ? <span className="text-amber-400">{c.missing_required} missing</span> : null}
                      {c.inactive && <span className="text-red-400">inactive</span>}
                    </div>
                    <select value={s.key} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); doMove([c.case_id], e.target.value); }}
                      data-testid={`move-${c.case_id}`} className="mt-1.5 w-full bg-slate-900 border border-slate-800 rounded text-[10px] text-slate-400 px-1 py-1 outline-none">
                      {board.stages.map((st) => <option key={st.key} value={st.key}>Move → {st.label}</option>)}
                    </select>
                    {c.cfi && (
                      <div className="mt-1.5 flex items-center justify-between text-[10px] border-t border-slate-800 pt-1" data-testid={`card-cfi-${c.case_id}`}>
                        <span className="text-emerald-400 font-semibold">{c.cfi.attorney_fee != null ? `$${Number(c.cfi.attorney_fee).toLocaleString(undefined, { maximumFractionDigits: 0 })} fee` : "—"}</span>
                        <span className="text-cyan-400">{c.cfi.confidence}%</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PANEL_META = {
  losing_value: "Cases Losing Value", inactive_14d: "Inactive 14+ Days", missing_police: "Missing Police Report",
  missing_medical: "Missing Medical Records", missing_signature: "Missing Signature", incomplete_intake: "Incomplete Intake",
  insurance_followup: "Insurance Follow-up", demand_nearly_complete: "Demand Nearly Complete", high_value_opportunities: "High-Value Opportunities",
};
function IntelligenceTab({ data, nav }) {
  if (!data) return <Spinner />;
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="cc-intelligence">
      {Object.entries(PANEL_META).map(([key, label]) => {
        const cards = data.panels[key] || [];
        return (
          <div key={key} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid={`intel-panel-${key}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-200 flex items-center gap-1.5"><Radar className="w-4 h-4 text-cyan-400" /> {label}</span>
              <span className="text-xs px-2 rounded-full bg-slate-800 text-slate-300">{data.panel_counts[key]}</span>
            </div>
            <div className="space-y-1 max-h-52 overflow-y-auto">
              {cards.length === 0 ? <p className="text-xs text-slate-500">Clear ✓</p> :
                cards.slice(0, 8).map((c) => (
                  <button key={c.case_id} onClick={() => nav(`/intelligence/${c.case_id}`)} className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded hover:bg-slate-800/60">
                    <span className="font-mono text-[11px] text-cyan-300">{c.case_number}</span>
                    <span className="text-[11px] text-slate-400">CRI {c.score}</span>
                  </button>
                ))}
            </div>
          </div>
        );
      })}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="intel-panel-duplicates">
        <span className="text-sm font-semibold text-slate-200 flex items-center gap-1.5 mb-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Duplicate Client Detection <span className="text-xs px-2 rounded-full bg-slate-800">{data.duplicate_clients.length}</span></span>
        {data.duplicate_clients.length === 0 ? <p className="text-xs text-slate-500">None detected ✓</p> :
          data.duplicate_clients.slice(0, 8).map((d) => (
            <div key={d.client} className="text-xs text-slate-300 py-1 border-b border-slate-800/60">{d.client} · {d.cases.length} cases</div>
          ))}
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="intel-panel-revenue">
        <span className="text-sm font-semibold text-slate-200 flex items-center gap-1.5 mb-2"><TrendingUp className="w-4 h-4 text-red-400" /> Revenue Anomalies <span className="text-xs px-2 rounded-full bg-slate-800">{data.revenue_anomalies.length}</span></span>
        {data.revenue_anomalies.length === 0 ? <p className="text-xs text-slate-500">None ✓</p> :
          data.revenue_anomalies.slice(0, 8).map((r) => (
            <div key={r.invoice_id} className="text-xs text-slate-300 py-1 border-b border-slate-800/60">{money(r.amount)} unpaid 30+ days</div>
          ))}
      </div>
    </div>
  );
}

const SEV = { critical: "text-red-400 bg-red-500/10", warning: "text-amber-400 bg-amber-500/10", positive: "text-emerald-400 bg-emerald-500/10" };
const PRI = { High: "bg-red-500", Medium: "bg-amber-500", Low: "bg-slate-500" };
function AlertsTab({ data, nav, reload }) {
  if (!data) return <Spinner />;
  const dismiss = async (key) => {
    try { await api.post(`/command-center/alerts/${encodeURIComponent(key)}/dismiss`); toast.success("Dismissed"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2" data-testid="cc-alerts">
      {data.length === 0 ? <p className="text-slate-500 text-sm">No active alerts. All clear ✓</p> :
        data.map((a) => (
          <div key={a.alert_key} data-testid={`alert-${a.type}`} className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-3">
            <span className={`w-2 h-2 rounded-full ${PRI[a.priority]}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SEV[a.severity] || SEV.warning}`}>{a.priority}</span>
                <span className="text-sm font-semibold text-white">{a.title}</span>
                <span className="font-mono text-[11px] text-cyan-300">{a.case_number}</span>
              </div>
              <div className="text-xs text-slate-400 mt-0.5">{a.detail} · <span className="text-slate-300">{a.recommended_action}</span></div>
            </div>
            <button onClick={() => nav(a.drill_to)} className="text-xs px-3 py-1.5 rounded-full bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20" data-testid={`alert-resolve-${a.type}`}>Resolve</button>
            <button onClick={() => dismiss(a.alert_key)} className="p-1.5 rounded-full text-slate-500 hover:text-white hover:bg-slate-800" data-testid={`alert-dismiss-${a.type}`}><X className="w-4 h-4" /></button>
          </div>
        ))}
    </div>
  );
}

function StaffTab({ data }) {
  if (!data) return <Spinner />;
  return (
    <div data-testid="cc-staff">
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <div className="grid grid-cols-[1fr,auto,auto,auto,auto,1fr] gap-3 px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
          <span>Staff</span><span>Cases</span><span>Demand Ready</span><span>Tasks</span><span>Avg CRI</span><span>Efficiency</span>
        </div>
        {data.staff.map((s) => (
          <div key={s.user_id} className="grid grid-cols-[1fr,auto,auto,auto,auto,1fr] gap-3 px-4 py-3 items-center border-b border-slate-800/50" data-testid={`staff-${s.user_id}`}>
            <div><div className="text-sm text-white">{s.name}</div><div className="text-[10px] text-slate-500 uppercase">{(s.role || "").replace("_", " ")}</div></div>
            <span className="text-slate-200">{s.cases_managed}</span>
            <span className="text-emerald-400">{s.demand_ready}</span>
            <span className="text-slate-200">{s.tasks_completed}</span>
            <span className="text-slate-200">{s.avg_cri}</span>
            <div className="flex items-center gap-2"><div className="flex-1 h-1.5 bg-slate-800 rounded-full"><div className="h-full bg-cyan-500 rounded-full" style={{ width: `${s.efficiency_score}%` }} /></div><span className="text-xs text-slate-400">{s.efficiency_score}</span></div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-3">Not yet tracked: {data.not_tracked.join(", ")}. <span className="text-slate-600">{data.not_tracked_hint}</span></p>
    </div>
  );
}

function AssistantTab() {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const endRef = useRef(null);
  const PROMPTS = ["What should my staff work on first today?", "Which cases are closest to demand?", "What is slowing my firm down?", "Which clients are losing value?"];
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question || busy) return;
    setMsgs((m) => [...m, { role: "user", text: question }]); setQ(""); setBusy(true);
    try {
      const { data } = await api.post("/command-center/assistant", { question, session_id: session });
      setSession(data.session_id);
      setMsgs((m) => [...m, { role: "ai", ...data }]);
    } catch (e) { setMsgs((m) => [...m, { role: "ai", answer: formatApiErrorDetail(e?.response?.data?.detail) || "Assistant unavailable.", recommended_actions: [] }]); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col h-[70vh]" data-testid="cc-assistant">
      <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        {msgs.length === 0 && (
          <div className="text-center mt-10">
            <Sparkles className="w-8 h-8 text-cyan-400 mx-auto" />
            <p className="text-slate-300 mt-2">Ask the AI Operations Assistant about your firm.</p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {PROMPTS.map((p) => <button key={p} onClick={() => ask(p)} data-testid="assistant-prompt" className="text-xs px-3 py-1.5 rounded-full border border-slate-700 text-slate-300 hover:border-cyan-500 hover:text-cyan-300">{p}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m, i) => m.role === "user" ? (
          <div key={i} className="flex justify-end"><div className="bg-cyan-600 text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%] text-sm">{m.text}</div></div>
        ) : (
          <div key={i} className="flex justify-start"><div className="bg-slate-800 text-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 max-w-[85%] text-sm" data-testid="assistant-response">
            <p className="whitespace-pre-wrap">{m.answer}</p>
            {m.reasoning && <p className="text-xs text-slate-400 mt-2 border-t border-slate-700 pt-2">{m.reasoning}</p>}
            {(m.recommended_actions || []).length > 0 && (
              <div className="mt-2 space-y-1">
                {m.recommended_actions.map((a, j) => (
                  <div key={j} className="flex items-center gap-2 text-xs"><span className={`w-1.5 h-1.5 rounded-full ${PRI[a.priority] || "bg-slate-500"}`} /><span className="text-slate-200">{a.action}</span>{a.owner && <span className="text-slate-500">· {a.owner}</span>}</div>
                ))}
              </div>
            )}
            {m.confidence !== undefined && <div className="text-[10px] text-cyan-400 mt-2">Confidence: {m.confidence}%</div>}
          </div></div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-slate-800 rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 text-cyan-400 animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} data-testid="assistant-input"
          placeholder="Ask about your firm's operations…" className="flex-1 bg-slate-900 border border-slate-800 rounded-full px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-500" />
        <button onClick={() => ask()} disabled={busy} data-testid="assistant-send" className="px-5 rounded-full bg-cyan-500 text-slate-950 font-semibold hover:bg-cyan-400 disabled:opacity-40 flex items-center gap-1.5"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const Spinner = () => <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-cyan-400 animate-spin" /></div>;

function FinancialsTab({ data, nav }) {
  if (!data) return <Spinner />;
  if (!data.can_view_dashboards) return <p className="text-slate-400 text-sm">You do not have access to firm financial dashboards.</p>;
  const s = data.summary, f = data.forecast;
  const maxM = Math.max(1, ...(data.monthly_forecast || []).map((x) => x.amount));
  const List = ({ title, rows, testid }) => (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid={testid}>
      <div className="text-sm font-semibold text-slate-200 mb-2">{title}</div>
      {(rows || []).length === 0 ? <p className="text-xs text-slate-500">No valued cases.</p> :
        rows.slice(0, 8).map((c) => (
          <button key={c.case_id} onClick={() => nav(`/intelligence/${c.case_id}`)} className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded hover:bg-slate-800/60">
            <span className="font-mono text-[11px] text-cyan-300">{c.case_number}</span>
            <span className="text-[11px] text-emerald-400">{money(c.attorney_fee)} <span className="text-slate-500">· {c.confidence}%</span></span>
          </button>
        ))}
    </div>
  );
  return (
    <div className="space-y-6" data-testid="cc-financials">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Projected Firm Revenue", s.projected_firm_revenue, "text-emerald-400"], ["Expected (weighted)", s.expected_revenue, "text-cyan-400"],
          ["High-Confidence Revenue", s.high_confidence_revenue, "text-cyan-400"], ["Revenue At Risk", s.revenue_at_risk, "text-red-400"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4" data-testid={`fin-kpi-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">{l}</div>
            <div className={`mt-1 text-2xl font-extrabold tabular-nums ${c}`}>{money(v)}</div>
          </div>
        ))}
      </div>

      {data.insights?.length > 0 && (
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4" data-testid="fin-insights">
          <div className="text-xs font-semibold text-cyan-300 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Zap className="w-4 h-4" /> Executive Insights</div>
          <ul className="space-y-1">{data.insights.map((i, k) => <li key={k} className="text-sm text-slate-200 flex items-start gap-2"><span className="text-cyan-400 mt-0.5">›</span>{i}</li>)}</ul>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-3">
        {[["Next 30 Days", f.next_30], ["Next 60 Days", f.next_60], ["Next 90 Days", f.next_90], ["Annual (weighted)", f.annual]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">{l}</div>
            <div className="mt-1 text-xl font-extrabold text-white tabular-nums">{money(v)}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <div className="text-sm font-semibold text-slate-200 mb-3">Monthly Forecast (weighted)</div>
          {(data.monthly_forecast || []).length === 0 ? <p className="text-xs text-slate-500">No dated revenue yet.</p> : (
            <div className="flex items-end gap-1.5 h-28">
              {data.monthly_forecast.map((m) => (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1" title={`${m.month}: ${money(m.amount)}`}>
                  <div className="w-full bg-cyan-500/70 rounded-t" style={{ height: `${(m.amount / maxM) * 100}%` }} />
                  <span className="text-[8px] text-slate-500">{m.month.slice(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="fin-by-attorney">
          <div className="text-sm font-semibold text-slate-200 mb-3">Revenue by Attorney</div>
          {(data.by_attorney || []).map((a) => (
            <div key={a.attorney_id} className="flex justify-between text-sm py-1 border-b border-slate-800/50"><span className="text-slate-300">{a.name}</span><span className="text-emerald-400">{money(a.amount)}</span></div>
          ))}
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            {[["Avg Case Value", s.avg_case_value], ["Avg Fee", s.avg_attorney_fee], ["Cases Valued", s.cases_valued]].map(([l, v]) => (
              <div key={l}><div className="text-sm font-bold text-white">{l === "Cases Valued" ? (v ?? 0) : money(v)}</div><div className="text-[9px] text-slate-500 uppercase">{l}</div></div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <List title="Largest Fee Opportunities" rows={data.largest_cases} testid="fin-largest" />
        <List title="Highest Confidence" rows={data.highest_confidence} testid="fin-confidence" />
        <List title="Revenue Waiting on Records" rows={data.waiting_on_records} testid="fin-waiting-records" />
      </div>
    </div>
  );
}

function RetainerIntelTab({ data, nav }) {
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;
  const c = data.cards || {};
  const firm = data.firm;
  const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
  const hrs = (v) => (v === null || v === undefined ? "—" : v >= 24 ? `${(v / 24).toFixed(1)}d` : `${v}h`);
  const CARDS = [["Sent Today", c.sent_today], ["Pending", c.pending_signatures], ["Viewed", c.viewed], ["Not Opened", c.not_opened], ["Abandoned", c.abandoned], ["Signed", c.signed]];
  return (
    <div data-testid="cc-retainer-intel">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {CARDS.map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4" data-testid={`ri-card-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{l}</div>
            <div className="mt-1 text-2xl font-extrabold text-white tabular-nums">{v ?? 0}</div>
          </div>
        ))}
      </div>

      {data.visibility === "status_only" && <p className="mt-4 text-xs text-slate-500">Limited access: engagement analytics are hidden for your role.</p>}
      {data.visibility === "delivery_only" && <p className="mt-4 text-xs text-slate-500">Limited access: delivery confirmation only.</p>}

      {firm && (
        <div className="mt-5 grid md:grid-cols-3 gap-3">
          {[["Signature Rate", pct(firm.signature_rate)], ["Open Rate", pct(firm.open_rate)], ["Abandonment Rate", pct(firm.abandonment_rate)], ["Conversion %", pct(firm.conversion_pct)], ["Avg Conversion Score", firm.avg_conversion_score != null ? `${firm.avg_conversion_score}%` : "—"], ["Avg Time to Sign", hrs(firm.avg_time_to_sign_hours)]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{l}</div>
              <div className="mt-1 text-xl font-extrabold text-cyan-400 tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      )}

      {data.conversion_bands && Object.keys(data.conversion_bands).length > 0 && (
        <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="ri-conversion-bands">
          <div className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-cyan-400" /> Conversion Score™ Distribution</div>
          <div className="flex flex-wrap gap-2">
            {[["ready_to_sign", "Ready to Sign", "bg-emerald-500/15 text-emerald-300"], ["high_probability", "High Probability", "bg-cyan-500/15 text-cyan-300"], ["needs_follow_up", "Needs Follow-up", "bg-amber-500/15 text-amber-300"], ["losing_interest", "Losing Interest", "bg-orange-500/15 text-orange-300"], ["cold_lead", "Cold Lead", "bg-red-500/15 text-red-300"]].map(([k, l, cls]) => (
              <span key={k} className={`px-3 py-1.5 rounded-full text-xs font-medium ${cls}`}>{l}: <span className="font-bold">{data.conversion_bands[k] || 0}</span></span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="ri-follow-up">
          <div className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-1.5"><Bell className="w-4 h-4 text-amber-400" /> Clients Requiring Follow-Up</div>
          {(data.follow_up_needed || []).length === 0 ? <p className="text-xs text-slate-500">No follow-ups needed right now.</p> :
            (data.follow_up_needed || []).map((r) => (
              <button key={r.retainer_id} onClick={() => nav(`/intelligence/${r.case_id}`)} className="w-full text-left px-2 py-2 rounded hover:bg-slate-800/60 border-b border-slate-800/50 last:border-0">
                <div className="flex items-center justify-between"><span className="font-mono text-[11px] text-cyan-300">{r.case_number}</span><span className="text-[11px] text-amber-300">{r.recommendation} · {r.score}%</span></div>
                <div className="text-[11px] text-slate-400 mt-0.5 flex items-center justify-between gap-2"><span className="truncate">{r.why}</span>{r.predicted_time_to_sign && <span className="shrink-0 text-slate-500">ETA {r.predicted_time_to_sign}</span>}</div>
              </button>
            ))}
        </div>
        {firm && (
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="ri-attorneys">
            <div className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-1.5"><Users className="w-4 h-4 text-cyan-400" /> Attorney Effectiveness</div>
            {(firm.attorneys || []).map((a) => (
              <div key={a.attorney_id} className="flex items-center justify-between px-2 py-1.5 text-sm">
                <span className="text-slate-200">{a.name}</span>
                <span className="text-[11px] text-slate-400">{a.signed}/{a.sent} · <span className="text-emerald-400">{a.signature_rate ?? 0}%</span></span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


const CONF_CLS = {
  confirmed: "bg-emerald-500/15 text-emerald-300",
  canceled: "bg-red-500/15 text-red-300",
  pending: "bg-amber-500/15 text-amber-300",
  none: "bg-slate-700/40 text-slate-300",
};
const STATUS_LABEL = {
  executed: "Executed", recorded: "Recorded", responded: "Answered",
  awaiting_confirmation: "Awaiting", canceled: "Canceled", denied: "Denied", failed: "Failed", unavailable: "Unavailable",
};

function VoiceAuditTab() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [analytics, setAnalytics] = useState(null);
  const [f, setF] = useState({ q: "", device: "", confirmation_status: "", status: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);
  const PAGE_SIZE = 25;

  const qs = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    Object.entries({ ...f, ...extra }).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [f]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const [{ data: list }, { data: an }] = await Promise.all([
        api.get(`/voice-audit?page=${page}&page_size=${PAGE_SIZE}&${qs()}`),
        api.get(`/voice-audit/analytics?${qs()}`),
      ]);
      setRows(list.rows); setTotal(list.total); setAnalytics(an);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); setRows([]); }
  }, [page, qs]);
  useEffect(() => { load(); }, [load]);

  const apply = () => { setPage(1); load(); };
  const reset = () => { setF({ q: "", device: "", confirmation_status: "", status: "", date_from: "", date_to: "" }); setPage(1); };
  const exportUrl = (fmt) => `${process.env.REACT_APP_BACKEND_URL}/api/voice-audit/export.${fmt}?auth=${getToken()}&${qs()}`;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => { api.get("/voice/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const toggleConfirm = async () => {
    try {
      const { data } = await api.put("/voice/config", { require_confirmation: !cfg.require_confirmation });
      setCfg(data); toast.success("Voice confirmation policy updated");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const A = analytics || {};
  const CARDS = [
    ["Interactions", A.total ?? 0, "text-white"],
    ["Actions Executed", A.actions_executed ?? 0, "text-emerald-400"],
    ["Recorded for Review", A.actions_recorded ?? 0, "text-cyan-400"],
    ["Attorney Confirmed", A.confirmed ?? 0, "text-emerald-400"],
    ["Canceled", A.canceled ?? 0, "text-red-400"],
  ];

  return (
    <div data-testid="cc-voice-audit">
      <div className="flex items-start gap-2 mb-4 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
        <ShieldCheck className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-300"><span className="text-cyan-300 font-semibold">Attorney Approval Model.</span> Every Crash Ready Voice™ interaction is logged immutably. No legal communication or action executes without explicit attorney confirmation.</p>
      </div>

      {isAdmin && cfg && (
        <div className="flex items-center gap-3 mb-4 rounded-xl border border-slate-800 bg-slate-900/60 p-3" data-testid="voice-config-row">
          <ShieldCheck className="w-4 h-4 text-cyan-300 shrink-0" />
          <div className="text-xs text-slate-300 flex-1">
            <span className="font-semibold text-white">Require confirmation for voice actions</span>
            <span className="block text-slate-500">When off, low-risk actions (notes, mark urgent) execute immediately. Claiming a case always requires explicit confirmation.</span>
          </div>
          <button onClick={toggleConfirm} data-testid="voice-config-toggle"
            className={`relative w-11 h-6 rounded-full transition-colors ${cfg.require_confirmation ? "bg-cyan-500" : "bg-slate-700"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${cfg.require_confirmation ? "left-5" : "left-0.5"}`} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {CARDS.map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4" data-testid={`va-kpi-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{l}</div>
            <div className={`mt-1 text-2xl font-extrabold tabular-nums ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && apply()}
          placeholder="Search command, client, case…" data-testid="va-search"
          className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500 min-w-[220px]" />
        <select value={f.device} onChange={(e) => setF({ ...f, device: e.target.value })} data-testid="va-device" className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">All devices</option>{["web", "mobile", "tablet", "glasses", "carplay", "android_auto"].map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={f.confirmation_status} onChange={(e) => setF({ ...f, confirmation_status: e.target.value })} data-testid="va-confirmation" className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">Any confirmation</option>{["confirmed", "canceled", "pending", "none"].map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="va-status" className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">Any outcome</option>{Object.keys(STATUS_LABEL).map((d) => <option key={d} value={d}>{STATUS_LABEL[d]}</option>)}
        </select>
        <input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="va-date-from" className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white" />
        <input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="va-date-to" className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white" />
        <button onClick={apply} data-testid="va-apply" className="px-4 py-2 rounded-lg bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400">Apply</button>
        <button onClick={reset} className="px-3 py-2 rounded-lg bg-slate-800 text-slate-300 text-sm hover:bg-slate-700">Reset</button>
        <div className="ml-auto flex items-center gap-2">
          {["csv", "xlsx", "pdf"].map((fmt) => (
            <a key={fmt} href={exportUrl(fmt)} data-testid={`va-export-${fmt}`} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-cyan-300 text-xs font-medium hover:bg-slate-700">
              <FileDown className="w-3.5 h-3.5" /> {fmt.toUpperCase()}
            </a>
          ))}
        </div>
      </div>

      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <p className="text-slate-500 text-sm py-10 text-center">No voice interactions match these filters.</p>
      ) : (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="grid grid-cols-[150px,1fr,120px,120px,90px] gap-3 px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
            <span>When</span><span>Command / Response</span><span>Action</span><span>Confirmation</span><span>Device</span>
          </div>
          {rows.map((r) => (
            <button key={r.audit_id} onClick={() => setDetail(r)} data-testid={`va-row-${r.audit_id}`}
              className="w-full text-left grid grid-cols-[150px,1fr,120px,120px,90px] gap-3 px-4 py-3 items-start border-b border-slate-800/50 hover:bg-slate-800/40">
              <span className="text-[11px] text-slate-400 tabular-nums">{new Date(r.at).toLocaleString()}</span>
              <span className="min-w-0">
                <span className="block text-sm text-slate-200 truncate">{r.command}</span>
                <span className="block text-[11px] text-slate-500 truncate">{r.result || r.ai_response}</span>
                {r.case_number && <span className="text-[10px] font-mono text-cyan-400">{r.case_number}</span>}
              </span>
              <span className="text-[11px] text-slate-300 capitalize">{(r.backend_action || "query").replace(/_/g, " ")}<span className="block text-[10px] text-slate-500">{STATUS_LABEL[r.status] || r.status}</span></span>
              <span><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CONF_CLS[r.confirmation_status] || CONF_CLS.none}`}>{r.confirmation_status}</span></span>
              <span className="text-[11px] text-slate-400 uppercase">{r.device || "web"}</span>
            </button>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-sm disabled:opacity-40">Prev</button>
          <span className="text-xs text-slate-400">Page {page} of {pages} · {total} records</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-sm disabled:opacity-40">Next</button>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setDetail(null)} data-testid="va-detail-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-white flex items-center gap-2"><Mic className="w-5 h-5 text-cyan-400" /> Voice Interaction</h3>
              <button onClick={() => setDetail(null)} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-800"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2 text-sm">
              {[["Attorney", detail.attorney_name], ["When", new Date(detail.at).toLocaleString()], ["Device", `${detail.device || "web"}${detail.platform ? ` · ${detail.platform}` : ""}`],
                ["App version", detail.app_version], ["IP", detail.ip], ["Case", detail.case_number], ["Client", detail.client_name],
                ["Intent", detail.intent], ["Action", detail.backend_action], ["Outcome", STATUS_LABEL[detail.status] || detail.status],
                ["Confirmation", detail.confirmation_status], ["Duration", detail.duration_ms != null ? `${detail.duration_ms} ms` : null]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-800/60 py-1.5"><span className="text-slate-500">{k}</span><span className="text-slate-200 text-right">{v || "—"}</span></div>
              ))}
              <div className="pt-2"><div className="text-slate-500 text-xs uppercase mb-1">Command</div><div className="text-slate-200 bg-slate-800/60 rounded-lg p-2">{detail.command}</div></div>
              <div><div className="text-slate-500 text-xs uppercase mb-1">AI Response</div><div className="text-slate-300 bg-slate-800/60 rounded-lg p-2">{detail.result || detail.ai_response}</div></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function CommandCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [clock, setClock] = useState(new Date());

  const ENDPOINTS = { overview: "/command-center/overview", financials: "/command-center/financials", pipeline: null, intelligence: "/command-center/intelligence", retainers: "/command-center/retainer-intelligence", voiceaudit: null, alerts: "/command-center/alerts", staff: "/command-center/staff", assistant: null };

  const loadTab = useCallback(async (t) => {
    const ep = ENDPOINTS[t];
    if (!ep) return;
    try { const { data: d } = await api.get(ep); setData((prev) => ({ ...prev, [t]: d })); } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);
  useEffect(() => {
    const poll = setInterval(() => { if (tab !== "assistant" && tab !== "pipeline") { setRefreshing(true); loadTab(tab).finally(() => setTimeout(() => setRefreshing(false), 500)); } }, POLL_MS);
    const clk = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(poll); clearInterval(clk); };
  }, [tab, loadTab]);

  const manualRefresh = () => { setRefreshing(true); loadTab(tab).finally(() => setTimeout(() => setRefreshing(false), 400)); };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="command-center">
      {/* Command bar */}
      <div className="sticky top-0 z-30 bg-slate-950/90 backdrop-blur border-b border-slate-800">
        <div className="flex items-center gap-4 px-4 md:px-6 py-3">
          <button onClick={() => navigate("/attorney")} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> Exit</button>
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" /></span>
            <span className="font-heading font-extrabold tracking-tight">Firm Command Center™</span>
          </div>
          <div className="ml-auto flex items-center gap-4 text-xs text-slate-400">
            <span className="hidden sm:block tabular-nums">{clock.toLocaleTimeString()}</span>
            <button onClick={manualRefresh} className="flex items-center gap-1.5 hover:text-white"><RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin text-cyan-400" : ""}`} /> Live</button>
          </div>
        </div>
        <div className="flex gap-1 px-4 md:px-6 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`cc-tab-${key}`}
              className={`flex items-center gap-2 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === key ? "border-cyan-400 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
              <Icon className="w-4 h-4" /> <span className="hidden md:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
        {tab === "overview" && <OverviewTab data={data.overview} go={setTab} />}
        {tab === "financials" && <FinancialsTab data={data.financials} nav={navigate} />}
        {tab === "pipeline" && <PipelineTab nav={navigate} />}
        {tab === "intelligence" && <IntelligenceTab data={data.intelligence} nav={navigate} />}
        {tab === "evidence" && <PremiumEvidenceWidget dark />}
        {tab === "retainers" && <RetainerIntelTab data={data.retainers} nav={navigate} />}
        {tab === "voiceaudit" && <VoiceAuditTab />}
        {tab === "alerts" && <AlertsTab data={data.alerts} nav={navigate} reload={() => loadTab("alerts")} />}
        {tab === "staff" && <StaffTab data={data.staff} />}
        {tab === "assistant" && <AssistantTab />}
      </div>
    </div>
  );
}
